import {useCallback, useEffect, useMemo, type ReactNode} from 'react';
import {
  AuthContext,
  AuthEntityTypes,
  clearStoredUser,
  colverAuthHooker,
  type AuthContextType,
  type AuthSid,
  type CurrentUser,
} from '@shared/api/auth';
import {flushProductEvents, userMetricsClient} from '@shared/api/user-metrics';
import {isPublicRoute} from '@app/router/public-routes';

/**
 * AuthProvider поверх внешнего OAuth2 (bcf).
 *
 * Поток:
 * 1. На mount дёргаем `GET /users/self` (colver-профиль): авторизует
 *    session-cookie OAuth2 (prod) или `X-Tests-User-Sid` (dev tests-wrapper).
 * 2. 401 → чистим localStorage; AuthGuard в роутере увидит !isAuthenticated
 *    и редиректнет на `/login` (там кнопка SSO-входа → `/_/api/oauth2/v1/login`).
 * 3. logout — сначала отмечаем выход в метриках (logout шлюза до бэка не
 *    доходит), затем hard redirect на `/_/api/oauth2/v1/logout` (bcf гасит сессию).
 *
 * ⚠️ Провайдер стоит ВЫШЕ RouterProvider в `App.tsx` — НЕ используем
 * `useNavigate`/`useLocation`; редиректы делает guard в роутере.
 *
 * Bypass-режим (`VITE_USE_MOCK=true` или `VITE_SKIP_AUTH=true`) оставлен
 * для локального запуска без бэка — отдаёт mock-юзера и не делает запросы.
 *
 * На публичных роутах (`PUBLIC_ROUTES`, например `/support`) запрос профиля
 * не выполняется: сессии там заведомо нет, а `/users/self` идёт в непубличный
 * бэкенд и словил бы редирект на SSO. Путь берём из `window.location`, т.к.
 * провайдер стоит выше роутера.
 */

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true';
const BYPASS_AUTH = USE_MOCK || SKIP_AUTH;

const MOCK_USER: CurrentUser = {
  sid: 'mock-user-sid',
  email: 'mock@colver.test',
  displayName: 'Mock User',
  isOperator: true,
  state: 'active',
};

const mockAuthSid = (sid: string): AuthSid => ({
  entityType: AuthEntityTypes.user,
  entitySid: sid,
  userSid: sid,
});

export const AuthProvider = ({children}: {children: ReactNode}) => {
  // Снимок на момент загрузки страницы: провайдер выше роутера и на
  // client-side навигацию не перерисовывается.
  const isPublic = isPublicRoute(window.location.pathname);
  const query = colverAuthHooker.useMe({enabled: !BYPASS_AUTH && !isPublic});

  const logout = useCallback(() => {
    // Выход в метриках — best-effort и до logout шлюза, пока его JWT действует
    // (заодно досылаем накопленные продуктовые события).
    // Затем hard redirect на bcf logout — гасит oauth2-сессию и чистит cookie.
    flushProductEvents(true);
    void userMetricsClient
      .endSession()
      .catch(() => undefined)
      .finally(() => colverAuthHooker.startLogout());
  }, []);

  // 401 от /users/self — чистим storage. Guard заметит отсутствие user.
  useEffect(() => {
    if (BYPASS_AUTH || isPublic) return;
    if (query.isError) {
      clearStoredUser();
    }
  }, [query.isError, isPublic]);

  const value = useMemo<AuthContextType>(() => {
    if (BYPASS_AUTH) {
      return {
        isLoading: false,
        isAuthenticated: true,
        currentUser: MOCK_USER,
        authSid: mockAuthSid(MOCK_USER.sid),
        error: null,
        logout,
      };
    }
    const user = query.data;
    const isAuthed = Boolean(user) && !query.isError;
    return {
      // Отключённый запрос вечно `isPending` — на публичном роуте это держало бы
      // приложение в скелетоне, поэтому loading там всегда false.
      isLoading: !isPublic && query.isPending,
      isAuthenticated: isAuthed,
      currentUser: isAuthed ? user : undefined,
      authSid: isAuthed && user ? mockAuthSid(user.sid) : undefined,
      error: query.error,
      logout,
    };
  }, [
    query.data,
    query.isPending,
    query.isError,
    query.error,
    isPublic,
    logout,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
